import { useTranslation } from "@privacy/translations";
import { useState } from "react";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { DatePicker } from "../../shared/DatePicker";
import * as FormDialog from "../../shared/FormDialog";
import type { CreateOrUpdateAgreementPayload } from "../../api/partners";
import { createOrUpdateAgreementMutationOptions } from "../../api/partners";
import { useMutation } from "@tanstack/react-query";

type CreateAndUpdateAgreementFormDialogProps = {
  onClose: () => void;
  partnerId: string;
} & (
  | { defaultValues: CreateOrUpdateAgreementPayload; agreementId: string }
  | { defaultValues?: undefined | Pick<CreateOrUpdateAgreementPayload, "type">; agreementId?: undefined }
);

export const CreateAndUpdateAgreementFormDialog = ({
  onClose,
  defaultValues,
  agreementId,
  partnerId,
}: CreateAndUpdateAgreementFormDialogProps) => {
  const { t } = useTranslation();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const createOrUpdateAgreementMutation = useMutation(
    createOrUpdateAgreementMutationOptions({ partnerId, agreementId })
  );

  const { control, setFocus, handleSubmit } = useForm<CreateOrUpdateAgreementPayload>({ defaultValues });

  FormDialog.useAutoFocusField(setFocus, "title");

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>
        {agreementId ? t("Partner.Agreement.Dialog.EditTitle") : t("Partner.Agreement.Dialog.CreateTitle")}
      </FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(data => createOrUpdateAgreementMutation.mutate(data, { onSuccess: onClose }))}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={createOrUpdateAgreementMutation.isError} />

          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="title"
              rules={{ required: t("Partner.Agreement.Dialog.TitleRequired") }}
              label={t("Partner.Agreement.Dialog.TitleLabel")}
              placeholder={t("Partner.Agreement.Dialog.TitlePlaceholder")}
            />
            <FormDialog.TextField
              control={control}
              name="url"
              rules={{ required: t("Partner.Agreement.Dialog.UrlRequired") }}
              label={t("Partner.Agreement.Dialog.UrlLabel")}
              placeholder={t("Partner.Agreement.Dialog.UrlPlaceholder")}
            />

            <FormDialog.ControlledField
              control={control}
              name="validUntil"
              label={t("Partner.Agreement.Dialog.ValidUntilLabel")}
            >
              {({ field: { ref, ...field }, fieldState, id }) => (
                <DatePicker
                  format="DD MMMM YYYY"
                  open={isDatePickerOpen}
                  onClose={() => setIsDatePickerOpen(false)}
                  closeOnSelect
                  slotProps={{
                    textField: {
                      ref,
                      id,
                      size: "small",
                      fullWidth: true,
                      error: Boolean(fieldState.error),
                      onFocus: e => {
                        e.target.blur();
                        setIsDatePickerOpen(true);
                      },
                      onClick: () => setIsDatePickerOpen(true),
                    },
                  }}
                  {...field}
                  onChange={value => field.onChange(value ? value.format("YYYY-MM-DD") : undefined)}
                  value={field.value ? dayjs(field.value) : null}
                />
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={createOrUpdateAgreementMutation.isPending}>
            {t("Partner.Agreement.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
